import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter text...', 
  height = '150px',
  autoBullets = false 
}) => {
  const quillRef = useRef(null);
  const hasInitialized = useRef(false);
  // Defer mount until after the React tree is committed to avoid concurrent rendering conflicts
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  }), []);

  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline',
    'align',
    'list'
  ], []);

  const handleFocus = useCallback(() => {
    if (!autoBullets || hasInitialized.current) return;
    
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    
    const text = editor.getText().trim();
    if (text === '') {
      editor.formatLine(0, 1, 'list', 'bullet');
      hasInitialized.current = true;
    }
  }, [autoBullets]);

  useEffect(() => {
    if (!value || value === '' || value === '<p><br></p>') {
      hasInitialized.current = false;
    }
  }, [value]);

  const editorStyles = `
    .rich-text-editor .ql-container { min-height: ${height}; font-size: 14px; }
    .rich-text-editor .ql-editor { min-height: ${height}; }
    .rich-text-editor .ql-toolbar { border-top-left-radius: 6px; border-top-right-radius: 6px; background-color: #f9fafb; border-color: #d1d5db; }
    .rich-text-editor .ql-container { border-bottom-left-radius: 6px; border-bottom-right-radius: 6px; border-color: #d1d5db; }
    .rich-text-editor .ql-editor.ql-blank::before { color: #9ca3af; font-style: normal; }
    .rich-text-editor .ql-editor:focus { outline: none; }
    .rich-text-editor:focus-within .ql-toolbar { border-color: #3b82f6; }
    .rich-text-editor:focus-within .ql-container { border-color: #3b82f6; }
  `;

  if (!mounted) {
    return (
      <div className="rich-text-editor">
        <style>{editorStyles}</style>
        <div style={{ 
          minHeight: `calc(${height} + 42px)`,
          border: '1px solid #d1d5db', 
          borderRadius: '6px', 
          padding: '12px',
          backgroundColor: '#fff'
        }}>
          <span style={{ color: '#9ca3af' }}>{placeholder}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rich-text-editor">
      <style>{editorStyles}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        onFocus={handleFocus}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;
